// src/lib/weather.js
// Open-Meteo API — completely free, no API key required
// Docs: https://open-meteo.com/en/docs

const BASE_URL = 'https://api.open-meteo.com/v1/forecast'

// ─── Get current conditions by lat/long ───────────────────────
export async function getCurrentWeather(latitude, longitude) {
  const params = new URLSearchParams({
    latitude,
    longitude,
    current: [
      'temperature_2m',
      'relative_humidity_2m',
      'wind_speed_10m',
      'wind_direction_10m',
      'surface_pressure',
    ].join(','),
    wind_speed_unit: 'mph',
    temperature_unit: 'fahrenheit',
    timezone: 'auto',
  })

  const res = await fetch(`${BASE_URL}?${params}`)
  if (!res.ok) throw new Error('Weather fetch failed')
  const data = await res.json()

  const c = data.current
  return {
    tempF:       Math.round(c.temperature_2m),
    humidity:    Math.round(c.relative_humidity_2m),
    windMph:     Math.round(c.wind_speed_10m),
    windAngle:   Math.round(c.wind_direction_10m),
    pressureHpa: Math.round(c.surface_pressure),
  }
}

// ─── Get elevation for a lat/long (for altitude input) ────────
export async function getElevation(latitude, longitude) {
  const params = new URLSearchParams({ latitude, longitude })
  const res = await fetch(`https://api.open-meteo.com/v1/elevation?${params}`)
  if (!res.ok) return null
  const data = await res.json()
  // Convert meters to feet
  return data.elevation ? Math.round(data.elevation[0] * 3.28084) : null
}

// ─── Get user's GPS coordinates ───────────────────────────────
export function getUserLocation() {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error('Geolocation not supported'))
      return
    }
    navigator.geolocation.getCurrentPosition(
      pos => resolve({
        latitude:  pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy:  pos.coords.accuracy,
      }),
      err => reject(err),
      { timeout: 8000, maximumAge: 300000 } // cache for 5 minutes
    )
  })
}

// ─── Full auto-fill: location → weather + elevation ───────────
export async function autoFillConditions() {
  const location = await getUserLocation()
  const [weather, altFt] = await Promise.all([
    getCurrentWeather(location.latitude, location.longitude),
    getElevation(location.latitude, location.longitude),
  ])
  return {
    ...weather,
    altFt: altFt || 0,
    latitude:  location.latitude,
    longitude: location.longitude,
  }
}

// ─── Wind direction degrees → readable label ──────────────────
export function windDirectionLabel(degrees) {
  const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW']
  return dirs[Math.round(degrees / 22.5) % 16]
}
